import { geoEqualEarth, geoPath } from 'd3-geo'
import { select } from 'd3-selection'
import type { ZoomBehavior } from 'd3-zoom'
import { zoom, zoomIdentity, zoomTransform } from 'd3-zoom'
import type { Feature, FeatureCollection } from 'geojson'
import { feature } from 'topojson-client'
import type { GeometryCollection, Topology } from 'topojson-specification'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import worldTopology from 'world-atlas/countries-110m.json'

export type TrafficGeoPoint = {
  lat: number
  lon: number
  weight: number
  ip: string
  /** GeoLite location line; matches log `ip_location` when present. */
  locationLabel?: string
  countryCode?: string
}

type TrafficWorldMapProps = {
  geoPoints: TrafficGeoPoint[]
  geoAvailable: boolean
  /** When set, clicking a dot sets log search to this location string (falls back to IP if no label). */
  onSelectGeoLocation?: (logQuery: string) => void
}

const PADDING = { top: 10, right: 10, bottom: 10, left: 10 }
const DEFAULT_W = 960
const DEFAULT_H = 480

const TRAFFIC_DOT_CLASS = 'traffic-map-dot'

/** Counter-scale dot radius by zoom factor so circles stay city-sized on screen when the map layer is scaled. */
function applyTrafficDotRadii(layer: SVGGElement, k: number) {
  const kk = Math.max(k, 0.001)
  select(layer)
    .selectAll<SVGCircleElement, unknown>(`circle.${TRAFFIC_DOT_CLASS}`)
    .each(function () {
      const base = Number(this.getAttribute('data-base-r'))
      if (!Number.isFinite(base)) return
      const r = Math.max(base / kk, 0.35)
      select(this).attr('r', r)
    })
}

function useMapSize() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H })

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const measure = () => {
      const w = el.clientWidth
      if (w <= 0) return
      const width = Math.floor(w)
      const height = Math.max(220, Math.floor(width * 0.5))
      setSize({ w: width, h: height })
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return { containerRef, size }
}

export function TrafficWorldMap({ geoPoints, geoAvailable, onSelectGeoLocation }: TrafficWorldMapProps) {
  const { containerRef, size } = useMapSize()
  const svgRef = useRef<SVGSVGElement | null>(null)
  const layerRef = useRef<SVGGElement | null>(null)
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null)

  const land = useMemo(() => {
    const topo = worldTopology as unknown as Topology
    const obj = worldTopology.objects.countries as GeometryCollection
    return feature(topo, obj) as FeatureCollection
  }, [])

  const { landPaths, circles } = useMemo(() => {
    const projection = geoEqualEarth()
    projection.fitExtent(
      [
        [PADDING.left, PADDING.top],
        [size.w - PADDING.right, size.h - PADDING.bottom],
      ],
      land,
    )
    const pathGen = geoPath(projection)
    const landPaths = land.features
      .map((f, i) => {
        const d = pathGen(f as Feature)
        return d ? { d, key: `land-${i}` } : null
      })
      .filter((row): row is { d: string; key: string } => row !== null)

    const maxWeight = Math.max(...geoPoints.map((p) => p.weight), 1)
    // Base radius at zoom k=1 (counter-scaled in applyTrafficDotRadii while zooming).
    const rFor = (w: number) => 2 + Math.sqrt(w / maxWeight) * 10

    const circles = geoPoints
      .map((p, i) => {
        const c = projection([p.lon, p.lat])
        if (!c) return null
        const logQuery = (p.locationLabel?.trim() || p.ip).trim()
        return {
          key: `pt-${i}-${logQuery}-${p.lat}-${p.lon}`,
          cx: c[0],
          cy: c[1],
          r: rFor(p.weight),
          logQuery,
        }
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)

    return { landPaths, circles }
  }, [geoPoints, land, size.w, size.h])

  useLayoutEffect(() => {
    if (!geoAvailable) return
    const svg = svgRef.current
    const layer = layerRef.current
    if (!svg || !layer) return

    const svgSel = select(svg)
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.65, 22])
      // Ignore pan/zoom gestures that start on dots so clicks can filter logs; also avoids re-init when parent re-renders.
      .filter((event) => {
        const target = event.target as Element | null
        if (target?.closest?.(`circle.${TRAFFIC_DOT_CLASS}`)) return false
        return (!event.ctrlKey || event.type === 'wheel') && !event.button
      })

    z.on('zoom', (event) => {
      select(layer).attr('transform', event.transform.toString())
      applyTrafficDotRadii(layer, event.transform.k)
    })

    zoomRef.current = z
    svgSel.call(z)
    svgSel.call(z.transform, zoomIdentity)
    applyTrafficDotRadii(layer, zoomTransform(svg).k)

    return () => {
      zoomRef.current = null
      svgSel.on('.zoom', null)
    }
    // Intentionally omit onSelectGeoIp: it changes when log filters update and would reset zoom to identity.
  }, [geoAvailable, size.w, size.h])

  // New circles after data / projection change — keep radii in sync with current zoom.
  useLayoutEffect(() => {
    if (!geoAvailable) return
    const svg = svgRef.current
    const layer = layerRef.current
    if (!svg || !layer) return
    applyTrafficDotRadii(layer, zoomTransform(svg).k)
  }, [geoAvailable, circles, size.w, size.h])

  const resetMapView = useCallback(() => {
    const svg = svgRef.current
    const z = zoomRef.current
    if (!svg || !z) return
    select(svg).transition().duration(220).call(z.transform, zoomIdentity)
  }, [])

  return (
    <section className="space-y-3" aria-label="Traffic world map">
      <div>
        <h2 className="font-headline text-lg font-semibold text-on-surface">Where requests come from</h2>
        <p className="mt-1 max-w-3xl font-mono text-xs leading-relaxed text-on-surface-variant">
          Approximate locations from client IP via a local MaxMind GeoLite2 database (city-level at best; VPNs and
          carrier NAT skew results). Circle size reflects request count for each public IP in the same filtered range as
          the charts above—not unique visitors. Click a dot to filter logs by that approximate location (same string as
          the Location column when GeoIP is filled; other IPs with the same label appear too).
        </p>
      </div>

      {!geoAvailable ? (
        <p className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-4 py-3 font-mono text-sm text-on-surface-variant">
          GeoIP map is disabled: add a GeoLite2-City <code className="text-on-surface">.mmdb</code> file (see README:
          GEOIP_DB_PATH / <code className="text-on-surface">data/geoip/</code>
          ).
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-xs text-on-surface-variant">
            <span>Scroll or pinch to zoom, drag to pan. Dots shrink with zoom so they stay closer to city scale.</span>
            <button
              type="button"
              onClick={resetMapView}
              className="key-btn-secondary shrink-0 px-3 py-1.5 font-mono text-xs"
            >
              Reset view
            </button>
          </div>
          <div
            ref={containerRef}
            className="w-full overflow-hidden rounded-xl border border-outline-variant/15 bg-surface-container-low"
          >
            <svg
              ref={svgRef}
              width="100%"
              height={size.h}
              viewBox={`0 0 ${size.w} ${size.h}`}
              className="block cursor-grab touch-none active:cursor-grabbing"
              role="img"
              aria-hidden={geoPoints.length === 0}
              aria-label="World map with request-weighted points by approximate IP location"
            >
              <g ref={layerRef}>
                <g className="fill-none stroke-outline-variant/70">
                  {landPaths.map(({ d, key }) => (
                    <path key={key} d={d} vectorEffect="non-scaling-stroke" strokeWidth={1.15} strokeLinejoin="round" />
                  ))}
                </g>
                <g>
                  {circles.map(({ key, cx, cy, r, logQuery }) => (
                    <circle
                      key={key}
                      role={onSelectGeoLocation ? 'button' : undefined}
                      tabIndex={onSelectGeoLocation ? 0 : undefined}
                      aria-label={
                        onSelectGeoLocation ? `Filter logs by location: ${logQuery}` : undefined
                      }
                      className={`fill-primary-container/85 ${TRAFFIC_DOT_CLASS} ${
                        onSelectGeoLocation
                          ? 'cursor-pointer focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-container/50'
                          : ''
                      }`}
                      cx={cx}
                      cy={cy}
                      r={r}
                      opacity={0.9}
                      data-base-r={r}
                      onClick={
                        onSelectGeoLocation
                          ? (e) => {
                              e.stopPropagation()
                              onSelectGeoLocation(logQuery)
                            }
                          : undefined
                      }
                      onKeyDown={
                        onSelectGeoLocation
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                e.stopPropagation()
                                onSelectGeoLocation(logQuery)
                              }
                            }
                          : undefined
                      }
                    />
                  ))}
                </g>
              </g>
            </svg>
          </div>
          {geoPoints.length === 0 ? (
            <p className="font-mono text-xs text-on-surface-variant">
              No public routable IPs with coordinates in this range for the current chart filters.
            </p>
          ) : null}
        </>
      )}
    </section>
  )
}
